import React, { useCallback, useRef, useState } from 'react';
import { App } from 'antd';

import AttestationModal from '../components/users/AttestationModal';
import { isStaleItem, saveItemTags } from '../services/items';
import { applyPrivateTags, removePrivateTags } from '../services/tagApplications';
import { needsAttestation } from '../services/mfa';
import { normalizeId } from '../utils/ids';
import {
	applyTagDelta,
	handTagIdsOf,
	privateTagRefusal,
	tagDroppedNote,
	tagWriteRefusal,
	withPrivateDelta,
} from '../utils/itemTags';

/**
 * TAGGING A SAVED ITEM, from the read view's chip row and the ⋯ "Tag…"
 * dialog (docs/plans/tags.md §3.3, §5.2). Two stores, two writes:
 *
 * - A COLLECTION or ITEM tag by hand goes in the item's own `tagIds`, through
 *   the form's write path: `PUT /api/items/{id}` with the whole list and the
 *   item's `updatedAt` (services/items saveItemTags). What is sent is THIS
 *   change — `{ add, remove }` — applied to the list the item carries. When
 *   somebody saved the item since the list read it (409 `stale_item`, the
 *   item as it stands in the answer), the change is applied again to THEIR
 *   list and sent once more: a tag somebody else put on is never taken off
 *   by a click on another. A second miss is said, not retried. A card that
 *   asks for a signature (428) opens its prompt and retries signed.
 * - A PRIVATE tag (or a private use of an item tag) goes through the
 *   person's own store, `PUT` / `DELETE /api/tag-applications`: read access
 *   is enough, nothing is written to the item or its History.
 *
 * Both are optimistic: the chip moves at once, and a refusal puts it back
 * with the API's reason in plain words (utils/itemTags.js). `onItemUpdated`
 * receives every state the item passes through — the page's feed puts it in
 * the row's place. A `tag_dropped` warning (a tag deleted since) is said.
 *
 * `setHand({ add, remove }, { titles })` — `titles`: item-tag titles the
 * picker knows (`{ id: { title, item_id, card_id } }`), shown until the
 * answer's own `tagTitles` arrive. `setPrivate({ add: [{ tagId, kind, name }],
 * remove: [{ tagId }] })`. Both resolve true when the change landed.
 */
export default function useItemTagWriter({ item, onItemUpdated, collectionId = null }) {
	const { message } = App.useApp();
	const [busy, setBusy] = useState(false);
	const [attestation, setAttestation] = useState(null);
	// The item as it stands on screen now — an optimistic change included —
	// so two clicks in a row build on each other rather than on a render ago.
	const itemRef = useRef(item);
	itemRef.current = item;

	const push = useCallback(next => {
		itemRef.current = next;
		if (typeof onItemUpdated === 'function') onItemUpdated(next);
	}, [onItemUpdated]);

	const withAttestation = useCallback((send) => send().catch(error => {
		if (!needsAttestation(error)) throw error;
		return new Promise((resolve, reject) => {
			setAttestation({
				mode: error?.response?.data?.mode || 'pin',
				cardId: normalizeId(itemRef.current?.card_id) || null,
				onSigned: token => send({ headers: { 'X-Attestation': token } }).then(resolve, reject),
				onCancel: () => reject(error),
			});
		});
	}), []);

	const setHand = useCallback(async (delta, { titles = null } = {}) => {
		const base = itemRef.current;
		const id = normalizeId(base?._id);
		if (!id) return false;
		const optimistic = {
			...base,
			tagIds: applyTagDelta(handTagIdsOf(base), delta),
			...(titles ? { tagTitles: { ...(base.tagTitles || {}), ...titles } } : {}),
		};
		push(optimistic);
		setBusy(true);
		const send = (current, config) => saveItemTags(id, applyTagDelta(handTagIdsOf(current), delta), {
			ifUpdatedAt: current?.updatedAt || null,
			config,
		});
		try {
			let answer;
			try {
				answer = await withAttestation(config => send(base, config));
			} catch (error) {
				const current = error?.response?.data?.item;
				if (!isStaleItem(error) || !current?._id) throw error;
				answer = await withAttestation(config => send(current, config));
			}
			const { warnings, ...saved } = answer || {};
			const latest = itemRef.current;
			push({
				...saved,
				// The PUT answer carries titles for what the item holds, never
				// the reader's private tags: those stay as the list read them.
				myTags: latest?.myTags,
				tagTitles: { ...(titles || {}), ...(saved.tagTitles || {}) },
			});
			const note = tagDroppedNote(warnings);
			if (note) message.warning(note);
			return true;
		} catch (error) {
			push(base);
			message.error(tagWriteRefusal(error));
			return false;
		} finally {
			setBusy(false);
		}
	}, [push, withAttestation, message]);

	const setPrivate = useCallback(async ({ add = [], remove = [] } = {}) => {
		const base = itemRef.current;
		const id = normalizeId(base?._id);
		if (!id || (!add.length && !remove.length)) return false;
		push({ ...base, myTags: withPrivateDelta(base.myTags, { add, remove }) });
		setBusy(true);
		const rows = list => list.map(entry => ({ tagId: entry.tagId, targetType: 'item', target_id: id }));
		try {
			if (add.length) await applyPrivateTags(rows(add));
			if (remove.length) await removePrivateTags(rows(remove));
			return true;
		} catch (error) {
			push({ ...itemRef.current, myTags: base.myTags });
			message.error(privateTagRefusal(error));
			return false;
		} finally {
			setBusy(false);
		}
	}, [push, message]);

	const modals = attestation ? (
		<AttestationModal
			open
			mode={attestation.mode}
			collectionId={collectionId}
			cardId={attestation.cardId}
			onSigned={token => { const { onSigned } = attestation; setAttestation(null); onSigned(token); }}
			onClose={() => { const { onCancel } = attestation; setAttestation(null); onCancel?.(); }}
		/>
	) : null;

	return { setHand, setPrivate, busy, modals };
}
